import React from "react";
import moment from "moment-timezone";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, ChevronRight, ChevronDown, Sparkles, Loader2, Square,
  RefreshCw, AlertCircle, Clock, Download, Briefcase, AppWindow, Globe,
  ArrowRight, Layers, Flame, ListChecks, Users, CalendarDays,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { useAiStream } from "../../hooks/useAiStream";
import { EmptyState } from "../DataTable";
import { Button } from "../primitives";
import AILimitModal from "../../../../components/AILimitModal/AILimitModal";
import AntDatePicker, { dayjs } from "../../../../components/UI/AntDatePicker";
import { downloadWorkSummaryPDF } from "../workSummaryPdf";
import { SERVER_URL } from "../../../../utils/serverConfig";
import { IST, buildWorkReport, fmtHuman } from "./reportModel";
import { useMotionPrefs, useCountUp, useStuck, EASE as MEASE } from "./motion";

/* The work report.

   Three layers, and the reader chooses when to go down one:

     Layer 1  the day at a glance — hours worked, the shape of the day, and a
              plain sentence. Free: it comes from a lookup, no AI, so opening
              someone's day costs nothing.
     Layer 2  the numbered list of what got done, each a sentence anyone can
              read. This is the layer that lets you decide whether to dig.
     Layer 3  per item, on request: what it consisted of, the files and tickets
              touched, and a minute-by-minute account.

   Charts earn their place by answering a question at a glance — the shape of
   the day, where the hours went, what kind of work it was. There is no focus
   score and no ring gauge, because neither survives the question "and what
   would I do differently if that number changed?" */

const fmtDate = (d) => moment(d).tz(IST).format("YYYY-MM-DD");
const EASE = [0.16, 1, 0.3, 1];

/* Read the calendar date the user actually clicked, straight off the Date's
   local fields. Converting through a timezone here would shift the day by one
   for anyone whose browser isn't on IST — they tap 8 August and get the 7th. */
const pickedDate = (d) => {
  if (!d) return null;
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export function WorkReport({ userId, memberLabel, memberControl }) {
  const t = useDashTheme();
  const mo = useMotionPrefs();
  const { ref: stickySentinel, stuck } = useStuck();

  const [date, setDate] = React.useState(() => fmtDate(Date.now() - 86400000));
  const [slots, setSlots] = React.useState([]);
  const [loadingDay, setLoadingDay] = React.useState(false);
  const [dayError, setDayError] = React.useState(null);
  const [expanded, setExpanded] = React.useState(() => new Set());
  const [hourReports, setHourReports] = React.useState({});
  const [queue, setQueue] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);

  const daily = useAiStream("/api/v1/work-summary/metrics-stream");
  const hourly = useAiStream("/api/v1/hourly-insights/metrics-stream");
  const running = React.useRef(null);

  // ── The day itself: instant, no AI. ──
  React.useEffect(() => {
    if (!userId || !date) return undefined;
    let alive = true;
    (async () => {
      setLoadingDay(true);
      setDayError(null);
      setSlots([]); setHourReports({}); setExpanded(new Set()); setQueue([]);
      running.current = null;
      daily.reset?.(); hourly.stop?.(); hourly.reset?.();
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${SERVER_URL}/api/v1/hourly-insights/slots?userId=${userId}&date=${date}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await res.json();
        if (!alive) return;
        if (data?.success && data.data?.slots) setSlots(data.data.slots);
        else setDayError(data?.message || "Couldn't open this day");
      } catch (_) {
        if (alive) setDayError("Couldn't open this day");
      } finally {
        if (alive) setLoadingDay(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, date]);

  // ── Minute-by-minute queue: one hour at a time, in order. ──
  React.useEffect(() => {
    const slot = running.current;
    if (!slot || hourly.streaming) return;
    // A failed or empty hour must still clear, or the queue stalls forever.
    if (hourly.error) setHourReports((p) => ({ ...p, [slot]: { report: null, metrics: null, empty: true } }));
    else if (hourly.report || hourly.metrics) setHourReports((p) => ({ ...p, [slot]: { report: hourly.report, metrics: hourly.metrics } }));
    else return;
    running.current = null;
    setQueue((q) => q.filter((s) => s !== slot));
  }, [hourly.streaming, hourly.report, hourly.metrics, hourly.error]);

  React.useEffect(() => {
    if (hourly.streaming || running.current || !queue.length) return;
    const next = queue.find((s) => !hourReports[s]);
    if (!next) { setQueue([]); return; }
    running.current = next;
    hourly.run({ userId, date, hourSlot: next, forceRegenerate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, hourly.streaming, hourReports]);

  const m = React.useMemo(
    () => buildWorkReport({ date, memberName: memberLabel, slots, metrics: daily.metrics, report: daily.report, hourReports }),
    [date, memberLabel, slots, daily.metrics, daily.report, hourReports],
  );

  const stepDay = (delta) => {
    const next = moment.tz(date, "YYYY-MM-DD", IST).add(delta, "day");
    if (next.isAfter(moment.tz(IST), "day")) return;
    setDate(next.format("YYYY-MM-DD"));
  };

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const breakDown = (item) => {
    setExpanded((prev) => new Set(prev).add(item.id));
    setQueue((q) => Array.from(new Set([...q, ...item.slots.filter((s) => !hourReports[s])])));
  };

  const savePdf = async () => {
    setSaving(true);
    try {
      await downloadWorkSummaryPDF({
        memberName: memberLabel || "Team member",
        dateLabel: m.dateLabel,
        report: daily.report,
        metrics: daily.metrics,
      });
    } catch (_) { /* the PDF is a convenience; never let it disturb the report */ }
    finally { setSaving(false); }
  };

  const isToday = date === fmtDate(Date.now());
  const noActivity = /no tracked activity|no activity/i.test(daily.error || "");

  // Everything in the side column comes from the written report. Before that
  // exists there is nothing to put there, so the column shouldn't be reserved.
  const hasSide =
    m.kinds.length > 0 || m.whereTime.length > 0 || m.tools.length > 0 ||
    m.sites.length > 0 || m.moves.length > 0 || m.quietPeriods.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.s[4] }}>
      {/* ── One control bar: who, which day, and the actions. Three stacked
             bands used to sit here doing the work of one; merged they cost
             ~52px instead of ~290px, and sticking them to the top means the
             controls stay reachable however far down the report you read. ── */}
      <span ref={stickySentinel} style={{ display: "block", height: 1, marginBottom: -1 }} />
      <div
        style={{
          position: "sticky", top: 0, zIndex: 5,
          display: "flex", alignItems: "center", gap: t.s[3],
          padding: `${t.s[2]}px ${t.s[3]}px`, borderRadius: t.r.lg,
          border: `1px solid ${stuck ? t.c.borderStrong : t.c.border}`,
          background: t.c.surfaceGlass, backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          boxShadow: stuck ? t.shadow.md : "none",
          transition: mo.reduce ? "none" : "box-shadow 220ms ease, border-color 220ms ease",
          flexWrap: "wrap",
        }}
      >
        {memberControl && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
              <Users size={14} style={{ color: t.c.textTertiary, flexShrink: 0 }} />
              {memberControl}
            </div>
            <span style={{ width: 1, height: 22, background: t.c.border, flexShrink: 0 }} />
          </>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: t.s[2], minWidth: 0 }}>
          <div style={{ display: "inline-flex", gap: 3 }}>
            <StepBtn t={t} onClick={() => stepDay(-1)} label="Previous day" reduce={mo.reduce}><ChevronLeft size={15} /></StepBtn>
            <StepBtn t={t} onClick={() => stepDay(1)} label="Next day" disabled={isToday} reduce={mo.reduce}><ChevronRight size={15} /></StepBtn>
          </div>
          {/* A real button is the trigger; Ant only owns the calendar panel.
              Styling Ant's own borderless input to look like a heading meant
              fighting its internal input sizing — which clipped the label and
              left the hit area somewhere other than the visible text. */}
          <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
            <motion.button
              type="button"
              onClick={() => setPickerOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={pickerOpen}
              aria-label={`Change date. Currently ${m.dateLabel}`}
              whileTap={mo.reduce ? undefined : { scale: 0.98 }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                height: 32, padding: "0 10px", borderRadius: t.r.md,
                border: `1px solid ${pickerOpen ? t.c.primary : "transparent"}`,
                background: pickerOpen ? t.c.surfaceMuted : "transparent",
                color: t.c.text, fontSize: 15, fontWeight: t.font.weight.medium,
                fontFamily: t.font.family, cursor: "pointer", whiteSpace: "nowrap",
                transition: mo.reduce ? "none" : "background 160ms ease, border-color 160ms ease",
              }}
              onMouseEnter={(e) => { if (!pickerOpen) e.currentTarget.style.background = t.c.surfaceMuted; }}
              onMouseLeave={(e) => { if (!pickerOpen) e.currentTarget.style.background = "transparent"; }}
            >
              {m.dateLabel}
              <CalendarDays size={14} style={{ color: t.c.textTertiary, flexShrink: 0 }} />
            </motion.button>

            {/* Invisible anchor: the panel portals to the body but positions
                itself against this element, so it drops under the button. */}
            <span style={{ position: "absolute", left: 0, bottom: 0, width: 1, height: 1, opacity: 0 }}>
              <AntDatePicker
                value={date}
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                onChange={(d) => {
                  const next = pickedDate(d);
                  if (next) setDate(next);
                  setPickerOpen(false);
                }}
                disabledDate={(cur) => cur && cur.isAfter(dayjs(), "day")}
                allowClear={false}
                inputReadOnly
                style={{ width: 1, padding: 0, border: "none" }}
              />
            </span>
          </div>
          {isToday && <Pill t={t} tone={t.status.positive}>Today</Pill>}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: t.s[2], marginLeft: "auto" }}>
          {daily.cached && !daily.streaming && m.hasWork && (
            <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>saved earlier</span>
          )}
          {daily.streaming ? (
            <Button variant="secondary" size="sm" leftIcon={<Square size={13} />} onClick={daily.stop}>Stop</Button>
          ) : m.hasWork ? (
            <>
              <Button variant="secondary" size="sm" leftIcon={<Download size={13} />} onClick={savePdf} disabled={saving}>
                {saving ? "Preparing…" : "Save as PDF"}
              </Button>
              <Button
                variant="secondary" size="sm" leftIcon={<RefreshCw size={13} />}
                onClick={() => daily.run({ userId, startDate: date, endDate: date, forceRegenerate: true })}
              >
                Redo
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {loadingDay ? (
        <ReportSkeleton t={t} />
      ) : !m.hasAny ? (
        <div style={{ borderRadius: t.r.lg, border: `1px solid ${t.c.border}`, background: t.c.surface }}>
          <EmptyState
            icon={<Clock size={22} />}
            title="No work recorded on this day"
            description={dayError || "Nothing came through for this date. Try another day."}
          />
        </div>
      ) : (
        <>
          {/* ═══ Layer 1 — the day at a glance. Hours, shape and stats all live
                in this one card now; they used to occupy three. ═══ */}
          <Hero t={t} m={m} mo={mo} />

          {/* ═══ Layers 2 & 3 ═══ */}
          <div
            style={{
              display: "grid", gap: t.s[4],
              // With nothing to put beside it, the report takes the full width
              // rather than sitting in a column next to a column of nothing.
              gridTemplateColumns: hasSide ? "minmax(0, 2.1fr) minmax(300px, 1fr)" : "minmax(0, 1fr)",
              alignItems: "start",
            }}
          >
            <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: t.s[3] }}>
              <SectionTitle t={t} icon={<ListChecks size={13} />}>What got done</SectionTitle>

              {m.hasWork ? (
                m.items.map((item, i) => (
                  <WorkItem
                    key={item.id} t={t} item={item} index={i} mo={mo}
                    streaming={daily.streaming}
                    open={expanded.has(item.id)}
                    onToggle={() => toggle(item.id)}
                    onBreakDown={() => breakDown(item)}
                    breakingDown={item.slots.some((s) => queue.includes(s)) || (hourly.streaming && item.slots.includes(running.current))}
                    hourReports={hourReports}
                  />
                ))
              ) : daily.streaming ? (
                <WritingState t={t} />
              ) : (
                <UnwrittenState
                  t={t} mo={mo} error={daily.error} noActivity={noActivity}
                  worked={m.workedLabel} stretches={m.counts.blocks}
                  onRun={() => daily.run({ userId, startDate: date, endDate: date, forceRegenerate: false })}
                />
              )}
            </div>

            {/* Side column — only mounted when it has something to say */}
            {hasSide && (
            <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: t.s[4] }}>
              {m.kinds.length > 0 && (
                <Panel t={t} icon={<Layers size={13} />} title="What kind of work">
                  <StackedBar t={t} parts={m.kinds} />
                </Panel>
              )}

              {m.whereTime.length > 0 && (
                <Panel t={t} icon={<Briefcase size={13} />} title={m.breakdownIsTools ? "Where the time went" : "Projects"}>
                  <BarList t={t} rows={m.whereTime.map((p) => ({ label: p.label, right: p.durationLabel, share: p.share, color: p.color }))} />
                </Panel>
              )}

              {m.tools.length > 0 && (
                <Panel t={t} icon={<AppWindow size={13} />} title="Programs used">
                  <BarList t={t} rows={m.tools.map((x) => ({ label: x.label, right: x.durationLabel, share: x.share, color: t.c.primary }))} />
                </Panel>
              )}

              {m.sites.length > 0 && (
                <Panel t={t} icon={<Globe size={13} />} title="Websites">
                  <BarList t={t} rows={m.sites.map((x) => ({ label: x.label, right: x.durationLabel, share: x.share, color: t.status.positive }))} />
                </Panel>
              )}

              {m.moves.length > 0 && (
                <Panel t={t} icon={<ArrowRight size={13} />} title="Information moved">
                  <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                    {m.moves.map((mv, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 14.5, flexWrap: "wrap" }}>
                        <span style={{ color: t.c.text }}>{mv.from}</span>
                        <ArrowRight size={13} style={{ color: t.c.textTertiary, flexShrink: 0 }} />
                        <span style={{ color: t.c.text }}>{mv.to}</span>
                        <span style={{ color: t.c.textTertiary, fontSize: 13 }}>
                          {mv.count > 1 ? `${mv.count} times` : "once"}
                        </span>
                      </div>
                    ))}
                  </div>
                </Panel>
              )}

              {m.quietPeriods.length > 0 && (
                <Panel t={t} icon={<Clock size={13} />} title="Quiet stretches">
                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                    {m.quietPeriods.map((q) => (
                      <div key={q.label} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 14.5 }}>
                        <span style={{ color: t.c.textSecondary }}>{q.label}</span>
                        <span style={{ color: t.c.textTertiary, flexShrink: 0 }}>{fmtHuman(q.minutes)}</span>
                      </div>
                    ))}
                  </div>
                </Panel>
              )}
            </div>
            )}
          </div>
        </>
      )}

      <AILimitModal
        isOpen={daily.limitReached || hourly.limitReached}
        onClose={() => { daily.setLimitReached(false); hourly.setLimitReached(false); }}
      />
    </div>
  );
}

/* ── Hero: the whole day in one card ── */
function Hero({ t, m, mo }) {
  // The one figure the page is about, so it is the one thing that counts up.
  const shown = useCountUp(m.workedMin);
  return (
    <motion.div
      {...mo.rise(0)}
      style={{
        borderRadius: 16, border: `1px solid ${t.c.border}`, boxShadow: t.shadow.sm, overflow: "hidden",
        background: t.isDark
          ? `linear-gradient(135deg, ${t.c.surface} 0%, rgba(109,74,255,0.10) 100%)`
          : `linear-gradient(135deg, #FFFFFF 0%, rgba(66,115,209,0.06) 100%)`,
      }}
    >
      {/* Text and chart share a row: on its own the chart was pushing the card
          ~120px taller for information that fits comfortably beside the words. */}
      <div
        style={{
          padding: `${t.s[5]}px ${t.s[6]}px ${t.s[4]}px`,
          display: "flex", alignItems: "flex-start", gap: t.s[6], flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 300px", minWidth: 0, maxWidth: 560 }}>
          <div style={{ fontSize: 13, color: t.c.textTertiary, marginBottom: 3 }}>{m.memberName}</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span
              style={{
                fontSize: 38, fontWeight: t.font.weight.semibold, color: t.c.text,
                letterSpacing: "-0.025em", lineHeight: 1.05,
                fontVariantNumeric: "tabular-nums", // stops the digits jittering as it counts
              }}
            >
              {fmtHuman(shown)}
            </span>
            <span style={{ fontSize: 16, color: t.c.textSecondary }}>of work</span>
          </div>
          {m.spanLabel && (
            <div style={{ fontSize: 15, color: t.c.textSecondary, marginTop: 7 }}>
              {m.spanLabel}
              {m.awayMin >= 15 && <span style={{ color: t.c.textTertiary }}> · {m.awayLabel} away from the desk</span>}
            </div>
          )}

          {/* The sentence sits under the figure it describes. Floated off to the
              right it read as an orphaned caption with no owner. */}
          {m.sentence && (
            <motion.p
              initial={mo.reduce ? false : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: mo.dur(0.14), duration: mo.dur(0.4), ease: MEASE }}
              style={{ margin: "14px 0 0", fontSize: 16.5, lineHeight: 1.6, color: t.c.text, maxWidth: 620 }}
            >
              {m.sentence}
            </motion.p>
          )}
        </div>

        <div style={{ flex: "1 1 360px", minWidth: 260, alignSelf: "flex-end" }}>
          <DayRhythm t={t} m={m} mo={mo} />
        </div>
      </div>

      <StatStrip t={t} m={m} mo={mo} />
    </motion.div>
  );
}

/* ── Day rhythm: 24 bars, one per hour. Answers "what shape was the day?"
      without asking anyone to learn anything. ── */
function DayRhythm({ t, m, mo }) {
  const hours = m.chartHours;
  const max = Math.max(15, ...hours.map((r) => r.minutes));
  const H = 52;
  const [hovered, setHovered] = React.useState(null);

  // Roughly four labels across whatever window we cropped to, always landing on
  // a real hour so the axis reads as clock time rather than arbitrary marks.
  const step = Math.max(1, Math.round(hours.length / 4));
  const ticks = new Set(hours.filter((_, i) => i % step === 0).map((r) => r.hour));

  return (
    <div>
      <div
        role="img"
        aria-label={`Work through the day. ${m.blocks.map((b) => `${b.label}, ${fmtHuman(b.minutes)}`).join(". ")}`}
        style={{ display: "flex", alignItems: "flex-end", gap: 3, height: H }}
      >
        {hours.map((r, i) => {
          const active = r.minutes > 0;
          const h = active ? Math.max(6, (r.minutes / max) * H) : 3;
          const strength = active ? 0.35 + (r.minutes / max) * 0.65 : 1;
          const isHot = hovered === r.hour;
          const dim = hovered != null && !isHot;
          return (
            <motion.div
              key={r.hour}
              // Bars spring up left-to-right: the day is drawn in the direction
              // it was lived. Capped stagger keeps the whole sweep under 400ms.
              initial={mo.reduce ? false : { height: 3, opacity: 0 }}
              animate={{
                height: h,
                opacity: mo.reduce ? 1 : (dim ? 0.3 : 1),
                scaleY: isHot ? 1.06 : 1,
              }}
              transition={
                mo.reduce
                  ? { duration: 0 }
                  : { delay: Math.min(i * 0.014, 0.28), ...mo.spring, opacity: { duration: 0.18 } }
              }
              onMouseEnter={() => setHovered(r.hour)}
              onMouseLeave={() => setHovered(null)}
              title={active ? `${r.label} · ${fmtHuman(r.minutes)}${r.projects.length ? ` · ${r.projects.join(", ")}` : ""}` : `${r.label} · nothing recorded`}
              style={{
                flex: 1, borderRadius: 3, minWidth: 0, transformOrigin: "bottom",
                background: active ? t.c.primary : t.c.border,
                filter: active ? `opacity(${strength})` : "opacity(0.55)",
                cursor: active ? "default" : "default",
              }}
            />
          );
        })}
      </div>
      <div style={{ display: "flex", marginTop: 5 }}>
        {hours.map((r) => (
          <span
            key={r.hour}
            style={{
              flex: 1, textAlign: "left", fontSize: 10.5, color: t.c.textTertiary,
              visibility: ticks.has(r.hour) ? "visible" : "hidden", whiteSpace: "nowrap",
            }}
          >
            {r.hour === 0 || r.hour === 24 ? "12am" : r.hour === 12 ? "noon" : r.hour > 12 ? `${r.hour - 12}pm` : `${r.hour}am`}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Stat tiles ── */
/* Stats live in the hero's footer rather than as four floating cards below it.
   Four separate cards cost a row of their own plus a gap — ~116px — to show
   four short numbers that belong to the day the card above already describes. */
function StatStrip({ t, m, mo }) {
  /* Only tiles we can actually fill. An em-dash where a number belongs reads as
     "broken", not "not yet" — so before the write-up exists the row shows what
     the free day lookup already knows, and swaps in the written figures once
     they arrive. The grid reflows either way. */
  const tiles = [];

  if (m.counts.items > 0) {
    tiles.push({ label: "Pieces of work", value: m.counts.items, icon: <ListChecks size={15} />, tone: t.c.primary });
  } else if (m.counts.activeHours > 0) {
    tiles.push({
      label: m.counts.activeHours === 1 ? "Hour with activity" : "Hours with activity",
      value: m.counts.activeHours, icon: <ListChecks size={15} />, tone: t.c.primary,
    });
  }

  if (m.busiest) {
    tiles.push({
      label: "Busiest hour", value: m.busiest.label,
      sub: fmtHuman(m.busiest.minutes), icon: <Flame size={15} />, tone: t.status.warning,
    });
  }

  if (m.counts.projects > 0) {
    tiles.push({
      label: m.breakdownIsTools ? "Programs" : (m.counts.projects === 1 ? "Project" : "Projects"),
      value: m.counts.projects, icon: <Briefcase size={15} />, tone: t.status.positive,
    });
  } else if (m.quietMin > 0) {
    tiles.push({
      label: "Quiet in between", value: fmtHuman(m.quietMin),
      icon: <Clock size={15} />, tone: t.status.positive,
    });
  }

  if (m.counts.blocks > 0) {
    tiles.push({
      label: m.counts.blocks === 1 ? "Work stretch" : "Work stretches",
      value: m.counts.blocks, icon: <Layers size={15} />, tone: t.c.ai,
    });
  }

  if (!tiles.length) return null;
  return (
    <div
      style={{
        display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(150px, 1fr))`,
        borderTop: `1px solid ${t.c.border}`,
        background: t.isDark ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.5)",
      }}
    >
      {tiles.map((x, i) => (
        <motion.div
          key={x.label}
          initial={mo.reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: mo.dur(0.1 + i * 0.05), duration: mo.dur(0.35) }}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: `${t.s[3]}px ${t.s[4]}px`,
            borderLeft: i === 0 ? "none" : `1px solid ${t.c.border}`,
            minWidth: 0,
          }}
        >
          <span
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28, borderRadius: 9, flexShrink: 0,
              background: `${x.tone}1A`, color: x.tone,
            }}
          >
            {x.icon}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: t.font.weight.semibold, color: t.c.text, lineHeight: 1.2, letterSpacing: "-0.01em" }}>
              {x.value}
            </div>
            <div
              style={{
                fontSize: 12, color: t.c.textTertiary, marginTop: 1,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >
              {x.label}{x.sub ? ` · ${x.sub}` : ""}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/* ── One piece of work ── */
function WorkItem({ t, item, index, mo, streaming, open, onToggle, onBreakDown, breakingDown, hourReports }) {
  const steps = item.steps || [];
  const allBrokenDown = item.slots.every((s) => hourReports[s]);

  return (
    <motion.div
      layout={mo.reduce ? false : "position"}
      // While the model is still writing, each new item springs in as it lands —
      // you watch the day get composed. Once written, it is a plain entrance.
      {...(streaming ? mo.arrive(index) : mo.rise(index))}
      whileHover={mo.reduce ? undefined : { y: -2, boxShadow: t.shadow.md }}
      style={{
        borderRadius: 14, border: `1px solid ${t.c.border}`, background: t.c.surface,
        boxShadow: t.shadow.sm, overflow: "hidden", display: "flex",
      }}
    >
      {/* Colour spine — the item's dominant kind of work */}
      <span style={{ width: 4, flexShrink: 0, background: item.color }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ padding: `${t.s[4]}px ${t.s[5]}px` }}>
          <div style={{ display: "flex", gap: t.s[4] }}>
            <span
              aria-hidden
              style={{
                flexShrink: 0, width: 30, height: 30, borderRadius: 9,
                background: `${item.color}1A`, color: item.color,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 15, fontWeight: t.font.weight.semibold,
              }}
            >
              {item.n}
            </span>

            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 19, fontWeight: t.font.weight.semibold, color: t.c.text, lineHeight: 1.3, letterSpacing: "-0.01em" }}>
                {item.title}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{item.whenLabel}</span>
                <Pill t={t} tone={item.color}>{item.durationLabel}</Pill>
                {item.project && <Pill t={t} tone={t.c.textSecondary} quiet>{item.project}</Pill>}
              </div>

              {item.summary && (
                <p style={{ margin: "12px 0 0", fontSize: 16, lineHeight: 1.65, color: t.c.text, maxWidth: 640 }}>
                  {item.summary}
                </p>
              )}

              {/* Composition strip — the one glance that says whether this was
                  one clean thing or a mix, before you open anything. */}
              {item.work.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: "flex", height: 6, borderRadius: 99, overflow: "hidden", gap: 2, maxWidth: 420 }}>
                    {item.work.map((w, i) => (
                      <motion.span
                        key={i}
                        initial={{ flexGrow: 0 }} animate={{ flexGrow: Math.max(w.share, 0.02) }}
                        transition={{ delay: 0.2 + i * 0.05, duration: 0.5, ease: EASE }}
                        style={{ flexBasis: 0, background: w.color, borderRadius: 3 }}
                      />
                    ))}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "5px 14px", marginTop: 8 }}>
                    {item.work.map((w, i) => (
                      <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: w.color, flexShrink: 0 }} />
                        <span style={{ fontSize: 13.5, color: t.c.textSecondary }}>{w.label}</span>
                        <span style={{ fontSize: 13, color: t.c.textTertiary }}>{w.pct}%</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={onToggle}
                aria-expanded={open}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5, marginTop: 14,
                  border: "none", background: "transparent", padding: 0,
                  color: t.c.primary, fontSize: 14.5, fontWeight: t.font.weight.medium,
                  fontFamily: t.font.family, cursor: "pointer",
                }}
              >
                <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }} style={{ display: "inline-flex" }}>
                  <ChevronDown size={15} />
                </motion.span>
                {open ? "Hide detail" : "Show detail"}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
              // The panel opens on a spring; exits are faster than entrances so
              // closing feels responsive rather than reluctant.
              transition={mo.reduce ? { duration: 0 } : { ...mo.springSoft, exit: { duration: 0.18 } }}
              style={{
                overflow: "hidden", borderTop: `1px solid ${t.c.border}`,
                background: t.isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
              }}
            >
              {/* Content arrives just behind the height so the text is never
                  seen being compressed — it lands into space already made. */}
              <motion.div
                initial={mo.reduce ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: mo.dur(0.08), duration: mo.dur(0.28), ease: MEASE }}
                style={{ padding: `${t.s[4]}px ${t.s[5]}px ${t.s[5]}px 74px` }}
              >
                {item.work.some((w) => w.detail) && (
                  <div style={{ marginBottom: t.s[5] }}>
                    <MicroLabel t={t}>What this involved</MicroLabel>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {item.work.map((w, i) => (
                        <div key={i} style={{ display: "flex", gap: 10 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: w.color, flexShrink: 0, marginTop: 7 }} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 15, fontWeight: t.font.weight.medium, color: t.c.text }}>
                              {w.label} <span style={{ color: t.c.textTertiary, fontWeight: 400 }}>· {w.pct}%</span>
                            </div>
                            {w.detail && <div style={{ fontSize: 15, color: t.c.textSecondary, lineHeight: 1.6, marginTop: 2 }}>{w.detail}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {item.tools.length > 0 && (
                  <div style={{ marginBottom: t.s[5] }}>
                    <MicroLabel t={t}>Programs used</MicroLabel>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {item.tools.map((x, i) => <Pill key={i} t={t} tone={t.c.textSecondary} quiet>{x}</Pill>)}
                    </div>
                  </div>
                )}

                <div>
                  <MicroLabel t={t}>Step by step</MicroLabel>
                  {steps.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {steps.map((s, i) => (
                        <div key={i} style={{ display: "flex", gap: t.s[4], paddingBottom: i === steps.length - 1 ? 0 : 16 }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, paddingTop: 5 }}>
                            <span style={{ width: 9, height: 9, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                            {i !== steps.length - 1 && <span style={{ width: 2, flex: 1, minHeight: 20, background: t.c.border, marginTop: 4 }} />}
                          </div>
                          <div style={{ fontSize: 14, color: t.c.textSecondary, width: 128, flexShrink: 0, fontVariantNumeric: "tabular-nums", paddingTop: 1 }}>
                            {s.whenLabel}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 15, color: t.c.text, fontWeight: t.font.weight.medium }}>{s.title}</div>
                            {s.summary && <div style={{ fontSize: 14.5, color: t.c.textSecondary, lineHeight: 1.6, marginTop: 2 }}>{s.summary}</div>}
                            {s.tools?.length > 0 && <div style={{ fontSize: 13.5, color: t.c.textTertiary, marginTop: 3 }}>{s.tools.join(", ")}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : breakingDown ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, color: t.c.textSecondary }}>
                      <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} style={{ display: "inline-flex" }}>
                        <Loader2 size={15} />
                      </motion.span>
                      Working through it minute by minute…
                    </div>
                  ) : allBrokenDown ? (
                    <div style={{ fontSize: 15, color: t.c.textTertiary }}>No further breakdown available for this stretch.</div>
                  ) : (
                    <button
                      onClick={onBreakDown}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 7, height: 34, padding: "0 14px",
                        borderRadius: t.r.md, border: `1px solid ${t.c.border}`, background: t.c.surface,
                        color: t.c.text, fontSize: 14.5, fontFamily: t.font.family, cursor: "pointer",
                      }}
                    >
                      <Sparkles size={14} style={{ color: t.c.primary }} />
                      Break this down minute by minute
                    </button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/* ── Charts ── */
function StackedBar({ t, parts }) {
  return (
    <div>
      <div style={{ display: "flex", height: 12, borderRadius: 99, overflow: "hidden", gap: 2 }}>
        {parts.map((p, i) => (
          <motion.span
            key={p.label}
            initial={{ flexGrow: 0 }} animate={{ flexGrow: Math.max(p.pct, 1) }}
            transition={{ delay: 0.1 + i * 0.05, duration: 0.55, ease: EASE }}
            title={`${p.label} · ${p.pct}%`}
            style={{ flexBasis: 0, background: p.color, borderRadius: 3 }}
          />
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
        {parts.map((p) => (
          <div key={p.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: p.color, flexShrink: 0 }} />
            <span style={{ fontSize: 14.5, color: t.c.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.label}</span>
            <span style={{ fontSize: 14, color: t.c.textSecondary, flexShrink: 0 }}>{p.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarList({ t, rows }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
      {rows.map((r, i) => (
        <div key={`${r.label}-${i}`}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 5 }}>
            <span style={{ fontSize: 14.5, color: t.c.text, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</span>
            <span style={{ fontSize: 14, color: t.c.textSecondary, flexShrink: 0 }}>{r.right}</span>
          </div>
          <div style={{ height: 7, borderRadius: 99, background: t.c.surfaceMuted, overflow: "hidden" }}>
            <motion.div
              initial={{ width: 0 }} animate={{ width: `${Math.max(2, Math.round(r.share * 100))}%` }}
              transition={{ delay: 0.05 + i * 0.05, duration: 0.55, ease: EASE }}
              style={{ height: "100%", background: r.color, borderRadius: 99 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── States ── */
/* Before the day has been written up.

   Previously a tall centred dashed box, which reads as an upload target or a
   crash — dashes say "something is missing here". This is a finished panel that
   happens to have one action in it: icon and copy on the left, the action on
   the right, the same shape as a work item so the page doesn't change character
   when the real content arrives. */
function UnwrittenState({ t, mo, onRun, error, noActivity, worked, stretches }) {
  const failed = error && !noActivity;
  const tone = failed ? t.status.negative : noActivity ? t.c.textSecondary : t.c.primary;

  const title = failed
    ? "That didn't go through"
    : noActivity
      ? "Not enough detail to write this day up"
      : "Write up what got done";

  const body = failed
    ? error
    : noActivity
      ? `The ${worked} above was recorded, but there wasn't enough underlying detail to describe the work itself.`
      : `${worked} was recorded${stretches > 1 ? ` across ${stretches} stretches` : ""}. Turn it into a plain-English account of each piece of work, with the files and tasks behind it.`;

  return (
    <motion.div
      {...mo.rise(0)}
      style={{
        borderRadius: 14, border: `1px solid ${t.c.border}`, boxShadow: t.shadow.sm,
        overflow: "hidden", display: "flex",
        background: t.isDark
          ? `linear-gradient(120deg, ${t.c.surface} 0%, rgba(109,74,255,0.07) 100%)`
          : `linear-gradient(120deg, #FFFFFF 0%, rgba(66,115,209,0.045) 100%)`,
      }}
    >
      <span style={{ width: 4, flexShrink: 0, background: tone, opacity: failed ? 1 : 0.5 }} />

      <div
        style={{
          flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: t.s[5],
          padding: `${t.s[5]}px ${t.s[5]}px`, flexWrap: "wrap",
        }}
      >
        <span
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 44, height: 44, borderRadius: 13, flexShrink: 0,
            background: `${tone}18`, color: tone,
          }}
        >
          {failed ? <AlertCircle size={21} /> : <Sparkles size={21} />}
        </span>

        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 18, fontWeight: t.font.weight.semibold, color: t.c.text, letterSpacing: "-0.01em" }}>
            {title}
          </div>
          <div style={{ fontSize: 15.5, color: t.c.textSecondary, marginTop: 5, lineHeight: 1.6, maxWidth: 560 }}>
            {body}
          </div>
        </div>

        {!noActivity && (
          <div style={{ flexShrink: 0 }}>
            <Button variant="primary" leftIcon={<Sparkles size={15} />} onClick={onRun}>
              {failed ? "Try again" : "Write up the day"}
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function WritingState({ t }) {
  return (
    <div style={{ borderRadius: 14, border: `1px solid ${t.c.border}`, background: t.c.surface, padding: t.s[7], display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <motion.span
        animate={{ scale: [1, 1.12, 1], opacity: [0.55, 1, 0.55] }} transition={{ duration: 1.5, repeat: Infinity }}
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 46, height: 46, borderRadius: 14, background: `${t.c.primary}18`, color: t.c.primary }}
      >
        <Sparkles size={22} />
      </motion.span>
      <div style={{ fontSize: 17, fontWeight: t.font.weight.medium, color: t.c.text }}>Reading through the day…</div>
      <div style={{ fontSize: 15, color: t.c.textTertiary }}>Each piece of work appears as it is written</div>
    </div>
  );
}

function ReportSkeleton({ t }) {
  const bar = (w, h = 14) => (
    <motion.div
      animate={{ opacity: [0.35, 0.7, 0.35] }} transition={{ duration: 1.4, repeat: Infinity }}
      style={{ height: h, width: w, borderRadius: 6, background: t.isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)" }}
    />
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.s[4] }}>
      <div style={{ borderRadius: 16, border: `1px solid ${t.c.border}`, background: t.c.surface, padding: t.s[6], display: "flex", flexDirection: "column", gap: 12 }}>
        {bar("24%", 30)}{bar("38%")}{bar("100%", 64)}
      </div>
      <div style={{ display: "grid", gap: t.s[3], gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} style={{ borderRadius: 14, border: `1px solid ${t.c.border}`, background: t.c.surface, padding: t.s[4] }}>{bar("70%", 22)}</div>
        ))}
      </div>
    </div>
  );
}

/* ── Small shared bits ── */
function SectionTitle({ t, icon, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 2 }}>
      <span style={{ display: "inline-flex", color: t.c.textTertiary }}>{icon}</span>
      <span style={{ fontSize: 12.5, letterSpacing: "0.08em", textTransform: "uppercase", color: t.c.textTertiary, fontWeight: t.font.weight.medium }}>
        {children}
      </span>
    </div>
  );
}

function MicroLabel({ t, children }) {
  return (
    <div style={{ fontSize: 12, letterSpacing: "0.07em", textTransform: "uppercase", color: t.c.textTertiary, marginBottom: 10, fontWeight: t.font.weight.medium }}>
      {children}
    </div>
  );
}

function Panel({ t, icon, title, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
      whileHover={{ boxShadow: t.shadow.md }}
      style={{ borderRadius: 14, border: `1px solid ${t.c.border}`, background: t.c.surface, boxShadow: t.shadow.sm, overflow: "hidden" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: `${t.s[3]}px ${t.s[4]}px`, borderBottom: `1px solid ${t.c.border}` }}>
        <span style={{ display: "inline-flex", color: t.c.textTertiary }}>{icon}</span>
        <span style={{ fontSize: 14, fontWeight: t.font.weight.medium, color: t.c.text }}>{title}</span>
      </div>
      <div style={{ padding: `${t.s[4]}px ${t.s[4]}px` }}>{children}</div>
    </motion.div>
  );
}

function Pill({ t, tone, quiet, children }) {
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", padding: "2px 9px", borderRadius: 99,
        background: quiet ? t.c.surfaceMuted : `${tone}18`,
        color: quiet ? t.c.textSecondary : tone,
        border: quiet ? `1px solid ${t.c.border}` : "none",
        fontSize: 13, fontWeight: t.font.weight.medium, whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function StepBtn({ t, onClick, label, disabled, reduce, children }) {
  return (
    <motion.button
      onClick={disabled ? undefined : onClick}
      aria-label={label} title={label} disabled={disabled}
      // Press feedback within one frame — the single cheapest thing that makes
      // a control feel physical rather than like a picture of a control.
      whileTap={disabled || reduce ? undefined : { scale: 0.92 }}
      whileHover={disabled || reduce ? undefined : { backgroundColor: t.c.surfaceHover }}
      transition={{ duration: 0.12 }}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32, borderRadius: t.r.md,
        border: `1px solid ${t.c.border}`, background: t.c.surface,
        color: disabled ? t.c.textTertiary : t.c.textSecondary,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </motion.button>
  );
}

export default WorkReport;
